import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";

import { useLazyListWarehousesQuery } from "store/services/warehouseApi";

import { useAppMeta, useTablePagination } from "hooks";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { BaseTabs } from "components/Shared/BaseTabs";
import ExportWarehouseExcel from "./warehouseComponents/ExportWarehouseExcel";

const WarehouseListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [
    {
      title: "Reports",
      path: "/app/crm/reports/list",
    },
    {
      title: "Warehouses",
    },
  ];

  const warehouseListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "ID",
      selector: (row) => row.warehouse_id,
      cell: (row) => (
        <div>
          <Link
            title={row?.warehouse_id}
            to={`/app/crm/warehouse/${row?._id}/detail`}
            className="hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {row?.warehouse_id ? (
              <HighlightedText text={row?.warehouse_id} highlight={keyword} />
            ) : (
              "N/A"
            )}
          </Link>
        </div>
      ),
    },
    {
      name: "Name",
      selector: (row) => row.name,
      cell: (row) => (
        <div className="text-center">
          <Link
            title={row?.name}
            to={`/app/crm/warehouse/${row?._id}/detail`}
            className="hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {row?.name ? (
              <HighlightedText text={row?.name} highlight={keyword} />
            ) : (
              "N/A"
            )}
          </Link>
        </div>
      ),
    },
    {
      name: "Branch",
      selector: (row) => row.branch?.name,
      cell: (row) => (
        <>
          {row?.branch?.name ? (
            <HighlightedText text={row?.branch?.name} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </>
      ),
    },

    {
      name: "Location",
      selector: (row) =>
        row.locationName?.locality + ", " + row.locationName?.city || "N/A",
      cell: (row) =>
        row?.locationName ? (
          <div>
            <span>{row.locationName.city}</span>
          </div>
        ) : (
          "N/A"
        ),
    },

    {
      name: "Capacity",
      selector: (row) => row.capacity,
      cell: (row) => <div>{row?.capacity ?? "N/A"}</div>,
    },
    {
      name: "Contact",
      selector: (row) => row?.contact_details,
      cell: (row) =>
        Array.isArray(row?.contact_details) && row.contact_details.length > 0
          ? row.contact_details
              .map((contact) => {
                // Remove text after last '-' in phoneNumber
                const phone = contact.phoneNumber;
                if (typeof phone === "string" && phone.includes("-")) {
                  return phone.substring(0, phone.lastIndexOf("-"));
                }
                return phone;
              })
              .join(", ")
          : "N/A",
    },
    {
      name: "Actions",
      center: true, // ✅ Center align column
      cell: (row) => (
        <div className="flex justify-center space-x-2">
          <Link to={`/app/crm/warehouse/${row._id}/detail`}>
            <IconButton
              size="small"
              aria-label="View Warehouse"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>

          <Link to={`/app/crm/warehouse/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit Warehouse">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </div>
      ),
      ignoreRowClick: true,
    },
  ];

  // RTK Query
  const [
    fetchWarehouses,
    { data: warehouseRes, isLoading, isFetching, error },
  ] = useLazyListWarehousesQuery();

  useEffect(() => {
    fetchWarehouses({
      active: activeTab !== "Inactive",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchWarehouses, keyword, perPage]);

  useAppMeta(`${activeTab} Warehouses`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={warehouseListTabs}
          activeTab={activeTab}
          actions={<ExportWarehouseExcel />}
        />

        <TableFilter
          searchLabel="Name/Location/Contact"
          disabled={isLoading || isFetching}
        >
          <div className="font-medium">{activeTab} Warehouses</div>
        </TableFilter>

        <AppTable
          columns={columns}
          data={warehouseRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={warehouseRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default WarehouseListScreen;

const PageActions = () => {
  return (
    <>
      <Link to="/app/crm/warehouse/create">
        <Button startIcon={<Add />} variant="contained">
          New Warehouse
        </Button>
      </Link>
    </>
  );
};
